import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fileUrl, asItems, asTotal, API } from "@/lib/api";
import { money, dt } from "@/lib/format";
import { PageHeader, Loader, EmptyState, Pagination } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CreditCard, Check, X, Download, CheckCircle2, XCircle, HelpCircle, RefreshCw, Bot, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// Fix M (2026-09-22): la cola muestra lo que la IA leyó del comprobante y el resultado de cada control.
const CHECK_LABELS = {
  es_comprobante: "Es un comprobante", monto: "Monto", moneda: "Moneda", cuenta_destino: "Cuenta de destino",
  fecha: "Fecha", referencia: "Referencia", imagen_unica: "Imagen", monto_maximo: "Monto máximo",
};

function Lectura({ p, onReleer, releyendo }) {
  const l = p.lectura_ia;
  const v = p.validacion;
  if (!l && !v) {
    return (
      <div className="rounded-xl bg-muted/60 px-3 py-2 mt-3 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">Sin lectura automática.</span>
        {p.comprobante_url && (
          <Button size="sm" variant="outline" className="rounded-full h-7 gap-1 text-xs" onClick={() => onReleer(p)} disabled={releyendo} data-testid={`releer-${p.id}`}>
            <RefreshCw className={`h-3.5 w-3.5 ${releyendo ? "animate-spin" : ""}`} /> Leer comprobante
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border px-3 py-2 mt-3 space-y-2" data-testid={`lectura-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold flex items-center gap-1"><Bot className="h-3.5 w-3.5 text-primary" /> Lectura automática</span>
        {p.comprobante_url && (
          <Button size="sm" variant="ghost" className="rounded-full h-7 gap-1 text-xs" onClick={() => onReleer(p)} disabled={releyendo} data-testid={`releer-${p.id}`}>
            <RefreshCw className={`h-3.5 w-3.5 ${releyendo ? "animate-spin" : ""}`} /> Volver a leer
          </Button>
        )}
      </div>
      {l && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
          {[["Monto", l.monto != null ? money(l.monto) : "—"], ["Fecha", [l.fecha, l.hora].filter(Boolean).join(" ") || "—"],
            ["Banco", [l.banco_origen, l.banco_destino].filter(Boolean).join(" → ") || "—"], ["Referencia", l.referencia || "—"],
            ["Cuenta de destino", l.cuenta_destino || "—"], ["Titular de destino", l.titular_destino || "—"]].map(([k, val]) => (
            <div key={k} className="min-w-0"><span className="text-muted-foreground">{k}: </span><span className="font-medium break-words">{val}</span></div>
          ))}
        </div>
      )}
      {v?.checks?.length > 0 && (
        <ul className="space-y-0.5" data-testid={`controles-${p.id}`}>
          {v.checks.map((c) => (
            <li key={c.id} className="flex items-start gap-1.5 text-xs">
              {c.ok === true ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                : c.ok === false ? <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                  : <HelpCircle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />}
              <span><span className="font-medium">{CHECK_LABELS[c.id] || c.id}:</span> {c.detalle}</span>
            </li>
          ))}
        </ul>
      )}
      {v && v.modo !== "automatica" && <p className="text-[11px] text-muted-foreground">Validación en modo {v.modo}: la aprobación la hace una persona.</p>}
    </div>
  );
}

export default function Payments() {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("abrir");
  const [pend, setPend] = useState(null);
  const [hist, setHist] = useState([]);
  const [histSkip, setHistSkip] = useState(0);
  const [histTotal, setHistTotal] = useState(0);
  const LIMIT = 50;
  const [comentarios, setComentarios] = useState({});
  const [releyendo, setReleyendo] = useState(null);

  const load = async () => {
    const [p, all] = await Promise.all([
      api.get("/payments", { params: { estado: "pendiente_validacion", limit: 200 } }),
      api.get("/payments", { params: { skip: histSkip, limit: LIMIT } }),
    ]);
    setPend(asItems(p));
    setHist(asItems(all).filter((x) => x.estado !== "pendiente_validacion"));
    setHistTotal(asTotal(all));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [histSkip]);
  useEffect(() => {
    api.post("/notifications/marcar-leidas", null, { params: { tipo: "pago_pendiente" } });
    api.post("/notifications/marcar-leidas", null, { params: { tipo: "pago_validado" } });
  }, []);

  const exportCsv = () => {
    const token = localStorage.getItem("ms_token");
    window.open(`${API}/payments/export?auth=${token}`, "_blank");
  };

  const validar = async (pago, estado) => {
    try {
      await api.post(`/payments/${pago.id}/validar`, { estado, comentario: comentarios[pago.id] || "" });
      toast.success(estado === "validado" ? "Pago validado ✓" : "Pago rechazado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const releer = async (p) => {
    setReleyendo(p.id);
    try {
      const { data } = await api.post(`/payments/${p.id}/releer`);
      toast.success(data.resultado === "validado" ? "Pago validado automáticamente ✓" : "Comprobante leído");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo leer el comprobante"); }
    finally { setReleyendo(null); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Pagos" subtitle="Comprobantes de transferencia: los que coinciden se validan solos; el resto queda acá para revisar.">
        <Button variant="outline" onClick={exportCsv} className="rounded-full gap-2" data-testid="export-payments-csv"><Download className="h-4 w-4" /> Exportar CSV</Button>
      </PageHeader>
      <Tabs defaultValue="cola">
        <TabsList className="rounded-full mb-4">
          <TabsTrigger value="cola" className="rounded-full" data-testid="tab-cola">Cola de validación {pend?.length ? `(${pend.length})` : ""}</TabsTrigger>
          <TabsTrigger value="historial" className="rounded-full" data-testid="tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="cola">
          {!pend ? <Loader /> : pend.length === 0 ? (
            <EmptyState icon={CreditCard} title="Nada por validar" subtitle="No hay comprobantes pendientes. ✨" />
          ) : (
            <div className="space-y-4">
              {pend.map((p) => (
                <div key={p.id} data-testid={`payment-${p.id}`} className={`ms-card overflow-hidden ${highlightId === p.id ? "ring-2 ring-primary" : ""}`}>
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-2/5 bg-muted flex items-center justify-center p-2">
                      {p.comprobante_url ? (
                        <a href={fileUrl(p.comprobante_url)} target="_blank" rel="noreferrer">
                          <img src={fileUrl(p.comprobante_url)} alt="comprobante" className="max-h-80 rounded-xl object-contain" />
                        </a>
                      ) : <span className="text-sm text-muted-foreground py-12">Sin imagen</span>}
                    </div>
                    <div className="flex-1 p-5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-head font-semibold">Pedido #{p.numero_pedido}</span>
                        <Badge className="bg-amber-50 text-amber-600 border-0 rounded-full capitalize">{p.tipo}</Badge>
                        {p.origen === "chat" && (
                          <Badge className="bg-accent text-accent-foreground border-0 rounded-full gap-1" data-testid={`origen-chat-${p.id}`}><MessageCircle className="h-3 w-3" /> Llegó por el chat</Badge>
                        )}
                      </div>
                      <p className="text-2xl font-head font-semibold text-primary mt-2">{money(p.monto)}</p>
                      <p className="text-xs text-muted-foreground">{dt(p.fecha)}</p>
                      <Lectura p={p} onReleer={releer} releyendo={releyendo === p.id} />
                      <Textarea placeholder="Comentario (opcional)…" className="rounded-xl mt-3"
                        value={comentarios[p.id] || ""} onChange={(e) => setComentarios({ ...comentarios, [p.id]: e.target.value })} data-testid={`payment-comment-${p.id}`} />
                      <div className="flex gap-2 mt-3">
                        <Button onClick={() => validar(p, "validado")} className="rounded-full bg-emerald-500 hover:bg-emerald-600 gap-1 flex-1" data-testid={`approve-${p.id}`}><Check className="h-4 w-4" /> Aprobar</Button>
                        <Button onClick={() => validar(p, "rechazado")} variant="outline" className="rounded-full text-red-500 border-red-200 gap-1 flex-1" data-testid={`reject-${p.id}`}><X className="h-4 w-4" /> Rechazar</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historial">
          {hist.length === 0 ? <EmptyState icon={CreditCard} title="Sin historial" subtitle="Los pagos validados o rechazados aparecerán acá." /> : (
            <div className="space-y-2">
              {hist.map((p) => (
                <div key={p.id} className={`ms-card p-4 flex items-center justify-between gap-2 ${highlightId === p.id ? "ring-2 ring-primary" : ""}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Pedido #{p.numero_pedido} · {money(p.monto)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{p.tipo} · {dt(p.fecha)} {p.validado_por ? `· ${p.validado_por}` : ""}</p>
                    {p.lectura_ia?.referencia && <p className="text-[11px] text-muted-foreground">Ref. {p.lectura_ia.referencia}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {p.validacion_automatica && <Badge className="rounded-full border-0 bg-accent text-accent-foreground gap-1" data-testid={`auto-${p.id}`}><Bot className="h-3 w-3" /> Automático</Badge>}
                    <Badge className={`rounded-full border-0 ${p.estado === "validado" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>{p.estado}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination skip={histSkip} limit={LIMIT} total={histTotal} onChange={setHistSkip} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
